<template>
  <button
    type="button"
    :class="buttonClasses"
    :style="style"
    :disabled="disabled"
    @click="onClick"
    @keydown="onKeyDown"
    @keyup="onKeyUp"
    @blur="onBlur"
  >
    <span v-if="iconBefore" class="flex items-center mr-2">
      <span v-if="isSvgIconBefore" v-html="iconBeforeWithSizeClass"></span>
    </span>

    <div class="flex items-center gap-1">
      <slot>
        <span>{{ label }}</span>
      </slot>
    </div>

    <span v-if="iconAfter" class="flex items-center ml-2">
      <span v-if="isSvgIconAfter" v-html="iconAfterWithSizeClass"></span>
    </span>
  </button>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";

const emit = defineEmits(["click"]);
const props = withDefaults(
  defineProps<{
    label?: string;
    size?: "extra-small" | "small" | "medium" | "large";
    type?: "Primary" | "Secondary" | "Alert";
    backgroundColor?: string;
    disabled?: boolean;
    iconBefore?: string | undefined;
    iconAfter?: string | undefined;
    outline?: boolean;
  }>(),
  {
    label: "",
    type: "Primary",
    disabled: false,
    size: "small"
  }
);

const size = computed(() => props.size || "medium");
const isKeyboardFocus = ref(false);

const buttonClasses = computed(() => {
  let baseClasses =
    "font-medium text-center rounded-lg border flex items-center justify-center";

  if (props.disabled) {
    baseClasses +=
      " bg-gray-200 text-gray-500 cursor-not-allowed opacity-50 dark:bg-gray-700 dark:text-gray-500";
  } else {
    if (props.outline) {
      if (props.type === "Alert") {
        baseClasses +=
          " text-red-600 border border-red-600 hover:text-white hover:bg-red-700 focus-visible:ring-4 focus-visible:ring-red-300 dark:border-red-600 dark:text-red-600 dark:hover:text-white dark:hover:bg-red-700 dark:focus-visible:ring-red-700";
      } else if (props.type === "Secondary") {
        baseClasses +=
          " text-gray-900 border border-gray-300 hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-4 focus-visible:ring-gray-100 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600 dark:hover:border-gray-600 dark:focus-visible:ring-gray-600";
      } else {
        baseClasses +=
          " text-black border border-blue-600 hover:text-white hover:bg-blue-700 focus-visible:ring-4 focus-visible:ring-blue-300 dark:border-blue-600 dark:text-blue-600 dark:hover:text-white dark:hover:bg-blue-700 dark:focus-visible:ring-blue-700";
      }
    } else if (props.type === "Primary") {
      baseClasses +=
        " text-white bg-blue-600 border-blue-600 hover:bg-blue-700 hover:border-blue-700 focus-visible:ring-blue-300 dark:bg-blue-600 dark:border-blue-600 dark:hover:bg-blue-700 dark:hover:border-blue-700 dark:focus-visible:ring-blue-700";
    } else if (props.type === "Secondary") {
      baseClasses +=
        " text-gray-900 bg-white border-transparent hover:bg-gray-100 focus-visible:ring-4 focus-visible:ring-gray-100 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-600 dark:focus-visible:ring-gray-600";
    } else if (props.type === "Alert") {
      baseClasses +=
        " text-white bg-red-600 hover:bg-red-700 focus-visible:ring-4 focus-visible:ring-red-300 dark:bg-red-600 dark:hover:bg-red-700 dark:focus-visible:ring-red-700";
    }
  }

  if (isKeyboardFocus.value) {
    baseClasses +=
      " focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500";
  }

  switch (size.value) {
    case "extra-small":
      baseClasses += " text-xs px-3 py-2";
      break;
    case "small":
      baseClasses += " text-sm px-3 py-2";
      break;
    case "medium":
      baseClasses += " text-base px-5 py-2.5";
      break;
    case "large":
      baseClasses += " text-lg px-5 py-3";
      break;
  }

  return baseClasses;
});

const style = computed(() => ({
  backgroundColor: props.type === "Primary" ? undefined : props.backgroundColor
}));

const isSvgIconBefore = computed(() => typeof props.iconBefore === "string");
const isSvgIconAfter = computed(() => typeof props.iconAfter === "string");

const iconSizeClass = computed(() => {
  switch (size.value) {
    case "extra-small":
      return "w-3 h-3";
    case "small":
      return "w-3 h-3";
    case "medium":
      return "w-3.5 h-3.5";
    case "large":
      return "w-4 h-4";
    default:
      return "w-3 h-3";
  }
});

const iconBeforeWithSizeClass = computed(() => {
  if (props.iconBefore) {
    return props.iconBefore.replace(
      /class="([^"]*)"/,
      `class="text-current ${iconSizeClass.value}"`
    );
  }
  return "";
});

const iconAfterWithSizeClass = computed(() => {
  if (props.iconAfter) {
    return props.iconAfter.replace(
      /class="([^"]*)"/,
      `class="text-current ${iconSizeClass.value}"`
    );
  }
  return "";
});

const onClick = () => {
  if (!props.disabled) {
    emit("click");
  }
};

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === "Tab") {
    isKeyboardFocus.value = true;
  }
};

const onKeyUp = (event: KeyboardEvent) => {
  if (event.key === "Tab") {
    isKeyboardFocus.value = true;
  }
};

const onBlur = () => {
  isKeyboardFocus.value = false;
};
</script>

<style scoped>
button:focus {
  outline: none; /* Remove focus outline on click */
}

button:focus-visible {
  outline: 2px solid #4f83cc; /* Customize for keyboard navigation */
  outline-offset: 2px;
}
</style>
